import "../css/AddStockist.css";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import Toaster, { showSuccessToast, showErrorToast } from "../dist/Toaster";
import CustomButton from "../pages/CustomButton";

const AddDistributor = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const id = query.get("id");
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    distributorName: "",
    contactPerson: "",
    country: "India",
    state: "",
    city: "",
    headquarter: "",
    division: "",
    employeeId: "",
    dob: "",
    marriageAnniversary: "",
    address: "",
    mobile: "",
    phone: "",
    email: "",
    pin: "",
    gstin: "",
    panNumber: "",
    dlNumber: "",
    cstNumber: "",
    mappingCode: "",
    addedDate: "",
    updatedBy: "",
    startTime: "",
    endTime: "",
    latitude: "",
    longitude: "",
    isActive: true,
  });

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [headquarters, setHeadquarters] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const init = async () => {
      try {
        // load lookups first
        const [statesRes, divisionsRes] = await Promise.all([
          fetch(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
          fetch(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() }),
        ]);

        const statesData = statesRes.ok ? await statesRes.json() : [];
        const divisionsData = divisionsRes.ok ? await divisionsRes.json() : [];

        setStates(statesData || []);
        setDivisions(divisionsData || []);

        if (isEditMode) {
          await fetchDistributorForEdit(id);
        }
      } catch (err) {
        console.error("Init error:", err);
      }
    };

    init();
  }, [id]); // depends on id so it reruns correctly when query changes

  const fetchDistributorForEdit = async (distributorId) => {
    try {
      const res = await fetch(`${BASE_URL}/api/distributors/${distributorId}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch distributor");
      const data = await res.json();

      // handle response that could be either an object or an array
      const d = Array.isArray(data) ? data[0] : data;
      if (!d) throw new Error("Distributor not found in response");
      console.log("Distributor fetched for edit:", d);

      // load dependent lists first so selects have options
      if (d.state_id) {
        const citiesRes = await fetch(`${BASE_URL}/api/cities/${d.state_id}`, {
          headers: getAuthHeaders(),
        });
        const citiesData = citiesRes.ok ? await citiesRes.json() : [];
        setCities(citiesData || []);
      }

      if (d.city_id) {
        const hqRes = await fetch(`${BASE_URL}/api/headquarters/${d.city_id}`, {
          headers: getAuthHeaders(),
        });
        const hqData = hqRes.ok ? await hqRes.json() : [];
        setHeadquarters(hqData || []);
      }

      // finally set form data (use strings for select values)
      setFormData({
        distributorName: d.distributor_name || "",
        contactPerson: d.contact_person || "",
        country: d.country || "India",
        state: d.state_id ? String(d.state_id) : "",
        city: d.city_id ? String(d.city_id) : "",
        headquarter: d.headquarter_id ? String(d.headquarter_id) : "",
        division: d.division_id ? String(d.division_id) : "",
        employeeId: d.employee_id ? String(d.employee_id) : "",
        mobile: d.mobile ? String(d.mobile) : "",
        phone: d.phone ? String(d.phone) : "",
        email: d.email || "",
        pin: d.pin || "",
        address: d.address || "",
        gstin: d.gstin || "",
        panNumber: d.pan_number || "",
        dlNumber: d.dl_number || "",
        cstNumber: d.cst_number || "",
        mappingCode: d.mapping_code ? String(d.mapping_code) : "",
        addedDate: d.added_date || "",
        updatedBy: d.updated_by || localStorage.getItem("loggedInUser") || "",
        startTime: d.start_time || "",
        endTime: d.end_time || "",
        latitude: d.latitude || "",
        longitude: d.longitude || "",
        // respect false values
        isActive: typeof d.is_active === "boolean" ? d.is_active : true,
        dob: d.dob || "",
        marriageAnniversary: d.marriage_anniversary || "",
      });

      console.log("formData after set:", {
        distributorName: d.distributor_name,
        state: d.state_id,
        city: d.city_id,
        headquarter: d.headquarter_id,
      });
    } catch (err) {
      console.error("Error loading distributor:", err);
      showErrorToast("Failed to load distributor for editing");
    }
  };

  // validation helpers
  const validateEmail = (email) => {
    if (!email) return true;
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  const validateMobile = (mobile) => {
    if (!mobile) return false;
    return /^\d{10}$/.test(mobile);
  };
  const getDateErrorMessage = (date, fieldName) => {
    if (!date) return "";
    const selectedDate = new Date(date);
    const today = new Date();
    if (fieldName === "dob") {
      const minDate = new Date();
      minDate.setFullYear(today.getFullYear() - 18);
      if (selectedDate > today) return "Date cannot be in the future";
      if (selectedDate > minDate) return "Person must be at least 18 years old";
    } else if (selectedDate > today) {
      return "Date cannot be in the future";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (
      ["mobile", "phone", "pin", "mappingCode", "employeeId"].includes(name)
    ) {
      if (value === "" || /^\d+$/.test(value)) {
        setFormData({ ...formData, [name]: value });
      }
    } else if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    setFormData({
      ...formData,
      state: selectedState,
      city: "",
      headquarter: "",
    });
    setCities([]);
    setHeadquarters([]);

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.city;
      delete newErrors.headquarter;
      return newErrors;
    });

    if (selectedState) {
      fetch(`${BASE_URL}/api/cities/${selectedState}`, {
        headers: getAuthHeaders(),
      })
        .then((res) => res.json())
        .then((data) => setCities(data || []))
        .catch((err) => console.error("Error fetching cities:", err));
    }
  };

  const handleCityChange = async (e) => {
    const selectedCity = e.target.value;
    setFormData({ ...formData, city: selectedCity, headquarter: "" });
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.headquarter;
      return newErrors;
    });

    if (selectedCity) {
      try {
        const response = await fetch(
          `${BASE_URL}/api/headquarters/${selectedCity}`,
          {
            headers: getAuthHeaders(),
          }
        );
        const data = await response.json();
        setHeadquarters(data || []);
        if (data && data.length > 0) {
          setFormData((prev) => ({ ...prev, headquarter: String(data[0].id) }));
        }
      } catch (err) {
        console.error("Error fetching headquarters:", err);
      }
    } else {
      setHeadquarters([]);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      "distributorName",
      "contactPerson",
      "state",
      "city",
      "headquarter",
      "division",
      "mobile",
    ];
    requiredFields.forEach((field) => {
      if (!formData[field]) newErrors[field] = "This field is required";
    });
    if (!validateMobile(formData.mobile)) {
      newErrors.mobile = formData.mobile
        ? "Mobile number must be 10 digits"
        : "Mobile number is required";
    }
    if (formData.email && !validateEmail(formData.email))
      newErrors.email = "Please enter a valid email address";
    const dobError = getDateErrorMessage(formData.dob, "dob");
    if (dobError) newErrors.dob = dobError;
    const anniversaryError = getDateErrorMessage(
      formData.marriageAnniversary,
      "marriageAnniversary"
    );
    if (anniversaryError) newErrors.marriageAnniversary = anniversaryError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showErrorToast("Please fix the errors in the form");
      return;
    }

    const formattedData = {
      distributor_name: formData.distributorName?.trim() || null,
      contact_person: formData.contactPerson?.trim() || null,
      country: formData.country || null,
      state_id: formData.state ? parseInt(formData.state, 10) : null,
      city_id: formData.city ? parseInt(formData.city, 10) : null,
      headquarter_id: formData.headquarter
        ? parseInt(formData.headquarter, 10)
        : null,
      division_id: formData.division ? parseInt(formData.division, 10) : null,
      employee_id: formData.employeeId
        ? parseInt(formData.employeeId, 10)
        : null,
      mobile: formData.mobile || null,
      phone: formData.phone || null,
      email: formData.email || null,
      pin: formData.pin || null,
      address: formData.address || null,
      gstin: formData.gstin || null,
      pan_number: formData.panNumber || null,
      dl_number: formData.dlNumber || null,
      cst_number: formData.cstNumber || null,
      mapping_code: formData.mappingCode
        ? parseInt(formData.mappingCode, 10)
        : null,
      added_date:
        formData.addedDate && String(formData.addedDate).trim() !== ""
          ? String(formData.addedDate).split("T")[0]
          : null,
      updated_by:
        formData.updatedBy || localStorage.getItem("loggedInUser") || null,
      start_time: formData.startTime || null,
      end_time: formData.endTime || null,
      latitude: formData.latitude || null,
      longitude: formData.longitude || null,
      is_active:
        typeof formData.isActive === "boolean" ? formData.isActive : null,
      dob: formData.dob || null,
      marriage_anniversary: formData.marriageAnniversary || null,
    };

    try {
      const endpoint = isEditMode
        ? `${BASE_URL}/api/distributors/${id}`
        : `${BASE_URL}/api/distributors`;
      const method = isEditMode ? "PUT" : "POST";

      const response = await fetch(endpoint, {
        method,
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail ||
            (isEditMode
              ? "Failed to update distributor"
              : "Failed to add distributor")
        );
      }

      showSuccessToast(
        isEditMode
          ? "Distributor updated successfully!"
          : "Distributor added successfully!"
      );
      setTimeout(() => navigate("/admin/add-distributors"), 1000);
    } catch (error) {
      console.error("Error:", error);
      showErrorToast(error.message || "Failed to connect to the server.");
    }
  };

  return (
   <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      <div className="">
        <Toaster />
          <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 ">
            <span className="text-blue-700">
              {isEditMode ? "Edit " : "Add "}
            </span>
            <span className="text-blue-500">Distributor</span>
          </h1>

        <form onSubmit={handleSubmit}>
          {/* Basic Details */}
          <fieldset className="section">
            <legend className="bg-blue-500 border rounded text-white px-3">Basic Details</legend>
            <div className="grid-container">
              <label className="required-label">Distributor Name</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="distributorName"
                  value={formData.distributorName}
                  onChange={handleChange}
                  className={errors.distributorName ? "input-error" : ""}
                />
                {errors.distributorName && (
                  <span className="validation-error">
                    {errors.distributorName}
                  </span>
                )}
              </div>

              <label className="required-label">CONTACT PERSON</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  className={errors.contactPerson ? "input-error" : ""}
                />
                {errors.contactPerson && (
                  <span className="validation-error">
                    {errors.contactPerson}
                  </span>
                )}
              </div>

              <label className="required-label">COUNTRY</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
              >
                <option value="India">INDIA</option>
              </select>

              <label className="required-label">STATE</label>
              <div className="input-field-container">
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleStateChange}
                  className={errors.state ? "input-error" : ""}
                >
                  <option value="">Select</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <span className="validation-error">{errors.state}</span>
                )}
              </div>

              <label className="required-label">CITY</label>
              <div className="input-field-container">
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleCityChange}
                  className={errors.city ? "input-error" : ""}
                >
                  <option value="">Select</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
                {errors.city && (
                  <span className="validation-error">{errors.city}</span>
                )}
              </div>

              <label className="required-label">HQ</label>
              <div className="input-field-container">
                <select
                  name="headquarter"
                  value={formData.headquarter}
                  onChange={handleChange}
                  className={errors.headquarter ? "input-error" : ""}
                >
                  {headquarters.length > 0 ? (
                    headquarters.map((hq) => (
                      <option key={hq.id} value={hq.id}>
                        {hq.name}
                      </option>
                    ))
                  ) : (
                    <option value="">No headquarters available</option>
                  )}
                </select>
                {errors.headquarter && (
                  <span className="validation-error">{errors.headquarter}</span>
                )}
              </div>

              <label className="required-label">DIVISION</label>
              <div className="input-field-container">
                <select
                  name="division"
                  value={formData.division}
                  onChange={handleChange}
                  className={errors.division ? "input-error" : ""}
                >
                  <option value="">Select</option>
                  {divisions.map((division) => (
                    <option key={division.id} value={division.id}>
                      {division.name}
                    </option>
                  ))}
                </select>
                {errors.division && (
                  <span className="validation-error">{errors.division}</span>
                )}
              </div>

              <label className="required-label">MOBILE</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  maxLength="10"
                  placeholder="Enter mobile number"
                  className={errors.mobile ? "input-error" : ""}
                />
                {errors.mobile && (
                  <span className="validation-error">{errors.mobile}</span>
                )}
              </div>
            </div>
          </fieldset>

          {/* Contact Details */}
          <fieldset className="section">
            <legend className="bg-blue-500 border rounded text-white px-3">Contact Details</legend>
            <div className="grid-container">
              <label>ADDRESS</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                className={errors.address ? "input-error" : ""}
              />

              <label>DATE OF BIRTH</label>
              <div className="input-field-container">
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                  className={errors.dob ? "input-error" : ""}
                />
                {errors.dob && (
                  <span className="validation-error">{errors.dob}</span>
                )}
              </div>

              <label>PHONE</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength="10"
                  className={errors.phone ? "input-error" : ""}
                />
                {errors.phone && (
                  <span className="validation-error">{errors.phone}</span>
                )}
              </div>

              <label>MARRIAGE ANNIVERSARY</label>
              <div className="input-field-container">
                <input
                  type="date"
                  name="marriageAnniversary"
                  value={formData.marriageAnniversary}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                  className={errors.marriageAnniversary ? "input-error" : ""}
                />
                {errors.marriageAnniversary && (
                  <span className="validation-error">
                    {errors.marriageAnniversary}
                  </span>
                )}
              </div>

              <label>EMAIL</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className={errors.email ? "input-error" : ""}
                />
                {errors.email && (
                  <span className="validation-error">{errors.email}</span>
                )}
              </div>

              <label>PIN</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="pin"
                  value={formData.pin}
                  onChange={handleChange}
                  maxLength="6"
                  className={errors.pin ? "input-error" : ""}
                />
                {errors.pin && (
                  <span className="validation-error">{errors.pin}</span>
                )}
              </div>
            </div>
          </fieldset>

          {/* Financial Details */}
          <fieldset className="section">
           <legend className="bg-blue-500 border rounded text-white px-3">Financial Details</legend>
            <div className="grid-container">
              <label>GSTIN</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="gstin"
                  value={formData.gstin}
                  onChange={handleChange}
                  maxLength="15"
                  className={errors.gstin ? "input-error" : ""}
                />
                {errors.gstin && (
                  <span className="validation-error">{errors.gstin}</span>
                )}
              </div>

              <label>PAN NUMBER</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleChange}
                  maxLength="10"
                  className={errors.panNumber ? "input-error" : ""}
                />
                {errors.panNumber && (
                  <span className="validation-error">{errors.panNumber}</span>
                )}
              </div>

              <label>DL NUMBER</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="dlNumber"
                  value={formData.dlNumber}
                  onChange={handleChange}
                  className={errors.dlNumber ? "input-error" : ""}
                />
                {errors.dlNumber && (
                  <span className="validation-error">{errors.dlNumber}</span>
                )}
              </div>

              <label>CST NUMBER</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="cstNumber"
                  value={formData.cstNumber}
                  onChange={handleChange}
                  className={errors.cstNumber ? "input-error" : ""}
                />
                {errors.cstNumber && (
                  <span className="validation-error">{errors.cstNumber}</span>
                )}
              </div>

              <label>MAPPING CODE</label>
              <div className="input-field-container">
                <input
                  type="text"
                  name="mappingCode"
                  value={formData.mappingCode}
                  onChange={handleChange}
                  className={errors.mappingCode ? "input-error" : ""}
                />
                {errors.mappingCode && (
                  <span className="validation-error">{errors.mappingCode}</span>
                )}
              </div>
            </div>
          </fieldset>

          {/* Buttons */}
          <div className="flex justify-center items-center">
            <CustomButton
              type="submit"
              className=""
              variant="go"
            >
              {isEditMode ? "Update" : "Submit"}
            </CustomButton>
            <CustomButton
              className=""
              variant="cancel"
              onClick={(e) => {
                e.preventDefault();
                navigate("/admin/view-distributors");
              }}
            >
              Return
            </CustomButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDistributor;
